import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch, parseJsonSafe } from '../utils/api';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { BsPlus, BsInstagram, BsFacebook, BsLinkedin, BsGlobe } from 'react-icons/bs';

const NETWORK_COLORS = {
  instagram: '#C13584',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  blog: '#2ECC71',
  multi: '#888888'
};

const SocialPage = () => {
  const token = localStorage.getItem('token');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [brandForm, setBrandForm] = useState({ id: null, name: '', description: '', primaryColor: '', secondaryColor: '' });
  const [profiles, setProfiles] = useState([]);
  const [profileModal, setProfileModal] = useState({ open: false, network: 'instagram', id: null, profileName: '', url: '', blogType: 'wordpress' });
  const [calendarItems, setCalendarItems] = useState([]);
  const [calendarFilters, setCalendarFilters] = useState({ instagram: true, facebook: true, linkedin: true, blog: true });
  const [eventModal, setEventModal] = useState({ open: false, mode: 'create', data: null });
  const [guidelines, setGuidelines] = useState({
    targetAudience: '',
    valueProposition: '',
    toneOfVoice: '',
    contentPillars: '',
    doAndDonts: '',
    services: '',
    customInstructions: ''
  });
  const [generateModal, setGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({ fromDate: '', toDate: '', networks: ['linkedin', 'instagram', 'facebook', 'blog'], goal: 'autoridad' });
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState({ brands: false, profiles: false, calendar: false, guidelines: false });
  const [saving, setSaving] = useState({ brand: false, profile: false, event: false, guidelines: false });
  const selectedBrandData = useMemo(() => brands.find((b) => b.id === selectedBrand), [brands, selectedBrand]);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const fetchBrands = async () => {
    setLoading((p) => ({ ...p, brands: true }));
    try {
      const res = await apiFetch('/api/social/brands', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) {
        const list = Array.isArray(data) ? data : [];
        setBrands(list);
        if (!selectedBrand && list.length) setSelectedBrand(list[0].id);
        if (selectedBrand && !list.find((b) => b.id === selectedBrand)) {
          setSelectedBrand(list[0]?.id || null);
        }
      } else {
        alert((data && data.message) || 'Error obteniendo marcas');
      }
    } catch (err) {
      console.error('Error cargando marcas', err);
      alert('Error de red obteniendo marcas');
    } finally {
      setLoading((p) => ({ ...p, brands: false }));
    }
  };

  const fetchProfiles = async (brandId) => {
    if (!brandId) return;
    setLoading((p) => ({ ...p, profiles: true }));
    try {
      const res = await apiFetch(`/api/social/brands/${brandId}/profiles`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) setProfiles(Array.isArray(data) ? data : []);
      else alert((data && data.message) || 'Error obteniendo perfiles');
    } catch (err) {
      console.error('Error cargando perfiles', err);
      alert('Error de red obteniendo perfiles');
    } finally {
      setLoading((p) => ({ ...p, profiles: false }));
    }
  };

  const fetchCalendar = async (brandId) => {
    if (!brandId) return;
    setLoading((p) => ({ ...p, calendar: true }));
    try {
      const res = await apiFetch(`/api/social/brands/${brandId}/calendar`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) setCalendarItems(Array.isArray(data) ? data : []);
      else alert((data && data.message) || 'Error obteniendo calendario');
    } catch (err) {
      console.error('Error cargando calendario', err);
      alert('Error de red obteniendo calendario');
    } finally {
      setLoading((p) => ({ ...p, calendar: false }));
    }
  };

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      setProfiles([]);
      setCalendarItems([]);
      setGuidelines({
        targetAudience: '',
        valueProposition: '',
        toneOfVoice: '',
        contentPillars: '',
        doAndDonts: '',
        services: '',
        customInstructions: ''
      });
      fetchProfiles(selectedBrand);
      fetchCalendar(selectedBrand);
      loadGuidelines(selectedBrand);
    } else {
      setProfiles([]);
      setCalendarItems([]);
      setGuidelines({
        targetAudience: '',
        valueProposition: '',
        toneOfVoice: '',
        contentPillars: '',
        doAndDonts: '',
        services: '',
        customInstructions: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand]);

  const loadGuidelines = async (brandId) => {
    setLoading((p) => ({ ...p, guidelines: true }));
    try {
      const res = await apiFetch(`/api/social/brands/${brandId}/guidelines`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && data) {
        setGuidelines({
          targetAudience: data.target_audience || '',
          valueProposition: data.value_proposition || '',
          toneOfVoice: data.tone_of_voice || '',
          contentPillars: data.content_pillars || '',
          doAndDonts: data.do_and_donts || '',
          services: data.services || '',
          customInstructions: data.custom_instructions || ''
        });
      } else if (!res.ok) {
        alert((data && data.message) || 'Error obteniendo guía');
      }
    } catch (err) {
      console.error('Error cargando guía', err);
      alert('Error de red obteniendo guía');
    } finally {
      setLoading((p) => ({ ...p, guidelines: false }));
    }
  };

  const saveBrand = async (e) => {
    e.preventDefault();
    setSaving((p) => ({ ...p, brand: true }));
    const isEdit = Boolean(brandForm.id);
    const path = isEdit ? `/api/social/brands/${brandForm.id}` : '/api/social/brands';
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(path, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(brandForm)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        alert((data && data.message) || 'Error guardando marca');
        return;
      }
      setBrandModalOpen(false);
      setBrandForm({ id: null, name: '', description: '', primaryColor: '', secondaryColor: '' });
      fetchBrands();
    } catch (err) {
      console.error('Error guardando marca', err);
      alert('Error de red guardando marca');
    } finally {
      setSaving((p) => ({ ...p, brand: false }));
    }
  };

  const deleteBrand = async (id) => {
    if (!window.confirm('¿Eliminar marca?')) return;
    setSaving((p) => ({ ...p, brand: true }));
    try {
      const res = await apiFetch(`/api/social/brands/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        alert((data && data.message) || 'Error eliminando marca');
        return;
      }
      if (selectedBrand === id) setSelectedBrand(null);
      fetchBrands();
    } catch (err) {
      console.error('Error eliminando marca', err);
      alert('Error de red eliminando marca');
    } finally {
      setSaving((p) => ({ ...p, brand: false }));
    }
  };

  const openProfileModal = (network, existing) => {
    if (existing) {
      setProfileModal({ open: true, network: existing.network, id: existing.id, profileName: existing.profile_name, url: existing.url, blogType: existing.blog_type || 'wordpress' });
    } else {
      setProfileModal({ open: true, network, id: null, profileName: '', url: '', blogType: 'wordpress' });
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!selectedBrand) return;
    setSaving((p) => ({ ...p, profile: true }));
    const isEdit = Boolean(profileModal.id);
    const path = isEdit ? `/api/social/profiles/${profileModal.id}` : `/api/social/brands/${selectedBrand}/profiles`;
    const method = isEdit ? 'PUT' : 'POST';
    const payload = {
      network: profileModal.network,
      profileName: profileModal.profileName,
      url: profileModal.url,
      blogType: profileModal.blogType
    };
    try {
      const res = await apiFetch(path, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        alert((data && data.message) || 'Error guardando perfil');
        return;
      }
      setProfileModal({ open: false, network: 'instagram', id: null, profileName: '', url: '', blogType: 'wordpress' });
      fetchProfiles(selectedBrand);
    } catch (err) {
      console.error('Error guardando perfil', err);
      alert('Error de red guardando perfil');
    } finally {
      setSaving((p) => ({ ...p, profile: false }));
    }
  };

  const deleteProfile = async () => {
    if (!profileModal.id) return;
    if (!window.confirm('¿Eliminar perfil?')) return;
    setSaving((p) => ({ ...p, profile: true }));
    try {
      const res = await apiFetch(`/api/social/profiles/${profileModal.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        alert((data && data.message) || 'Error eliminando perfil');
        return;
      }
      setProfileModal({ open: false, network: 'instagram', id: null, profileName: '', url: '', blogType: 'wordpress' });
      fetchProfiles(selectedBrand);
    } catch (err) {
      console.error('Error eliminando perfil', err);
      alert('Error de red eliminando perfil');
    } finally {
      setSaving((p) => ({ ...p, profile: false }));
    }
  };

  const calendarEvents = useMemo(() => {
    return calendarItems
      .filter((c) => calendarFilters[c.network] || c.network === 'multi')
      .map((c) => ({
        id: c.id,
        title: c.title,
        start: c.date,
        backgroundColor: NETWORK_COLORS[c.network] || '#888',
        borderColor: NETWORK_COLORS[c.network] || '#888',
        extendedProps: c
      }));
  }, [calendarItems, calendarFilters]);

  const openCreateEvent = (dateStr) => {
    setEventModal({
      open: true,
      mode: 'create',
      data: { date: dateStr, time: '', network: 'instagram', content_type: 'post', title: '', summary: '', notes: '', status: 'idea' }
    });
  };

  const openEditEvent = (eventInfo) => {
    const c = eventInfo.event.extendedProps;
    setEventModal({
      open: true,
      mode: 'edit',
      data: {
        ...c,
        date: c.date,
        time: c.time || '',
        status: c.status || 'idea',
        content_type: c.content_type || c.contentType || 'post'
      }
    });
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    if (!selectedBrand) return;
    setSaving((p) => ({ ...p, event: true }));
    const d = eventModal.data;
    const isEdit = eventModal.mode === 'edit' && d.id;
    const path = isEdit ? `/api/social/calendar/${d.id}` : `/api/social/brands/${selectedBrand}/calendar`;
    const method = isEdit ? 'PUT' : 'POST';
    const payload = {
      date: d.date,
      time: d.time || null,
      network: d.network,
      contentType: d.content_type || d.contentType,
      title: d.title,
      summary: d.summary || '',
      notes: d.notes || '',
      status: d.status || 'idea'
    };
    try {
      const res = await apiFetch(path, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        alert((data && data.message) || 'Error guardando publicación');
        return;
      }
      setEventModal({ open: false, mode: 'create', data: null });
      fetchCalendar(selectedBrand);
    } catch (err) {
      console.error('Error guardando publicación', err);
      alert('Error de red guardando publicación');
    } finally {
      setSaving((p) => ({ ...p, event: false }));
    }
  };

  const deleteEvent = async () => {
    const d = eventModal.data;
    if (!d?.id) return;
    if (!window.confirm('¿Eliminar publicación?')) return;
    setSaving((p) => ({ ...p, event: true }));
    try {
      const res = await apiFetch(`/api/social/calendar/${d.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        alert((data && data.message) || 'Error eliminando publicación');
        return;
      }
      setEventModal({ open: false, mode: 'create', data: null });
      fetchCalendar(selectedBrand);
    } catch (err) {
      console.error('Error eliminando publicación', err);
      alert('Error de red eliminando publicación');
    } finally {
      setSaving((p) => ({ ...p, event: false }));
    }
  };

  const duplicateEvent = async () => {
    const d = eventModal.data;
    if (!d?.id || !selectedBrand) return;
    const payload = {
      date: d.date,
      time: d.time || null,
      network: d.network,
      contentType: d.content_type || d.contentType,
      title: `${d.title} (copia)`,
      summary: d.summary || '',
      notes: d.notes || '',
      status: 'idea'
    };
    setSaving((p) => ({ ...p, event: true }));
    try {
      const res = await apiFetch(`/api/social/brands/${selectedBrand}/calendar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        alert((data && data.message) || 'Error duplicando publicación');
        return;
      }
      fetchCalendar(selectedBrand);
    } catch (err) {
      console.error('Error duplicando publicación', err);
      alert('Error de red duplicando publicación');
    } finally {
      setSaving((p) => ({ ...p, event: false }));
    }
  };

  const regenerateEvent = async (bestVariantIndex = 0) => {
    if (!eventModal.data?.id) return;
    setSaving((p) => ({ ...p, event: true }));
    try {
      const res = await apiFetch(`/api/social/calendar/${eventModal.data.id}/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ bestVariantIndex })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'Error regenerando');
      // actualiza modal y calendario
      setEventModal((p) => ({ ...p, data }));
      fetchCalendar(selectedBrand);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving((p) => ({ ...p, event: false }));
    }
  };

  const handleDateClick = (info) => {
    if (!selectedBrand) {
      alert('Selecciona una marca para crear publicaciones');
      return;
    }
    openCreateEvent(info.dateStr);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Marcas</h5>
                <button className="btn btn-sm btn-outline-primary" onClick={() => { setBrandForm({ id: null, name: '', description: '', primaryColor: '', secondaryColor: '' }); setBrandModalOpen(true); }}>
                  <BsPlus /> Añadir
                </button>
              </div>
              {loading.brands && <div className="text-muted small mb-2">Cargando marcas...</div>}
              <div className="list-group">
                {brands.map((b) => (
                  <button key={b.id} className={`list-group-item list-group-item-action ${selectedBrand === b.id ? 'active' : ''}`} onClick={() => setSelectedBrand(b.id)}>
                    {b.name}
                  </button>
                ))}
                {!brands.length && <div className="text-muted">Sin marcas.</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-9">
          {selectedBrand ? (
            <>
              <div className="card shadow-sm mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 className="mb-0">{selectedBrandData?.name || 'Marca'}</h4>
                      <div className="text-muted">{selectedBrandData?.description}</div>
                    </div>
                    <div>
                      <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => {
                        const b = selectedBrandData;
                        if (!b) return;
                        setBrandForm({
                          id: b.id,
                          name: b.name,
                          description: b.description || '',
                          primaryColor: b.primary_color || '',
                          secondaryColor: b.secondary_color || ''
                        });
                        setBrandModalOpen(true);
                      }} disabled={saving.brand}>Editar</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => deleteBrand(selectedBrand)} disabled={saving.brand}>Eliminar</button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm mb-3">
                <div className="card-body d-flex gap-3 flex-wrap">
                  {loading.profiles && <div className="text-muted small">Cargando perfiles...</div>}
                  {[
                    { key: 'instagram', icon: <BsInstagram />, label: 'Instagram' },
                    { key: 'facebook', icon: <BsFacebook />, label: 'Facebook' },
                    { key: 'linkedin', icon: <BsLinkedin />, label: 'LinkedIn' },
                    { key: 'blog', icon: <BsGlobe />, label: 'Blog' }
                  ].map((net) => {
                    const existing = profiles.find((p) => p.network === net.key);
                    const active = Boolean(existing);
                    return (
                      <button
                        key={net.key}
                        className={`btn ${active ? 'btn-outline-primary' : 'btn-outline-secondary'} d-flex flex-column align-items-center`}
                        onClick={() => openProfileModal(net.key, existing)}
                      >
                        <span style={{ fontSize: '1.5rem' }}>{net.icon}</span>
                        <small>{active ? (existing.profile_name || existing.url) : 'Añadir perfil'}</small>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex gap-2 mb-2 flex-wrap">
                    {['instagram','facebook','linkedin','blog'].map((n) => (
                      <label key={n} className="form-check form-check-inline">
                        <input className="form-check-input" type="checkbox" checked={calendarFilters[n]} onChange={() => setCalendarFilters((p) => ({ ...p, [n]: !p[n] }))} />
                        <span className="form-check-label text-capitalize">{n}</span>
                      </label>
                    ))}
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setCalendarFilters({ instagram: true, facebook: true, linkedin: true, blog: true })}>Todo</button>
                    <button className="btn btn-sm btn-outline-primary ms-auto" onClick={() => setGenerateModal(true)}>Generar semana con IA</button>
                  </div>
                  {loading.calendar && <div className="text-muted small mb-2">Cargando calendario...</div>}
                  <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    events={calendarEvents}
                    dateClick={handleDateClick}
                    eventClick={openEditEvent}
                    locale={esLocale}
                    firstDay={1}
                    height="auto"
                  />
                </div>
              </div>
              <div className="card shadow-sm mt-3">
                <div className="card-body">
                  <h5>Guía de contenido (IA)</h5>
                  {loading.guidelines && <div className="text-muted small">Cargando guía...</div>}
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label">Público objetivo</label>
                      <textarea className="form-control" value={guidelines.targetAudience} onChange={(e) => setGuidelines((p) => ({ ...p, targetAudience: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Propuesta de valor</label>
                      <textarea className="form-control" value={guidelines.valueProposition} onChange={(e) => setGuidelines((p) => ({ ...p, valueProposition: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Servicios</label>
                      <textarea className="form-control" value={guidelines.services} onChange={(e) => setGuidelines((p) => ({ ...p, services: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Tono de voz</label>
                      <textarea className="form-control" value={guidelines.toneOfVoice} onChange={(e) => setGuidelines((p) => ({ ...p, toneOfVoice: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Pilares de contenido</label>
                      <textarea className="form-control" value={guidelines.contentPillars} onChange={(e) => setGuidelines((p) => ({ ...p, contentPillars: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Do & Don'ts</label>
                      <textarea className="form-control" value={guidelines.doAndDonts} onChange={(e) => setGuidelines((p) => ({ ...p, doAndDonts: e.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Instrucciones adicionales</label>
                      <textarea className="form-control" value={guidelines.customInstructions} onChange={(e) => setGuidelines((p) => ({ ...p, customInstructions: e.target.value }))} />
                    </div>
                    <div className="col-12 text-end">
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={saving.guidelines}
                        onClick={async () => {
                          setSaving((p) => ({ ...p, guidelines: true }));
                          try {
                            const res = await apiFetch(`/api/social/brands/${selectedBrand}/guidelines`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify(guidelines)
                            });
                            const data = await parseJsonSafe(res);
                            if (res.status === 401) return handleUnauthorized();
                            if (!res.ok) alert((data && data.message) || 'Error guardando guía');
                            else alert('Guía guardada');
                          } catch (err) {
                            console.error('Error guardando guía', err);
                            alert('Error de red guardando guía');
                          } finally {
                            setSaving((p) => ({ ...p, guidelines: false }));
                          }
                        }}
                      >
                        {saving.guidelines ? 'Guardando...' : 'Guardar guía'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </>
          ) : (
            <div className="alert alert-info">Selecciona o crea una marca para empezar.</div>
          )}
        </div>
      </div>

      {brandModalOpen && (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{brandForm.id ? 'Editar marca' : 'Nueva marca'}</h5>
                <button type="button" className="btn-close" onClick={() => setBrandModalOpen(false)}></button>
              </div>
              <form onSubmit={saveBrand}>
                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Nombre</label>
                    <input className="form-control" value={brandForm.name} onChange={(e) => setBrandForm((p) => ({ ...p, name: e.target.value }))} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Descripción</label>
                    <textarea className="form-control" value={brandForm.description} onChange={(e) => setBrandForm((p) => ({ ...p, description: e.target.value }))} />
                  </div>
                  <div className="row g-2">
                    <div className="col">
                      <label className="form-label">Color primario</label>
                      <input className="form-control" type="color" value={brandForm.primaryColor} onChange={(e) => setBrandForm((p) => ({ ...p, primaryColor: e.target.value }))} />
                    </div>
                    <div className="col">
                      <label className="form-label">Color secundario</label>
                      <input className="form-control" type="color" value={brandForm.secondaryColor} onChange={(e) => setBrandForm((p) => ({ ...p, secondaryColor: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setBrandModalOpen(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={saving.brand}>{saving.brand ? 'Guardando...' : 'Guardar'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {generateModal && (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Generar plan con IA</h5>
                <button type="button" className="btn-close" onClick={() => setGenerateModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Desde</label>
                    <input className="form-control" type="date" value={generateForm.fromDate} onChange={(e) => setGenerateForm((p) => ({ ...p, fromDate: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Hasta</label>
                    <input className="form-control" type="date" value={generateForm.toDate} onChange={(e) => setGenerateForm((p) => ({ ...p, toDate: e.target.value }))} />
                  </div>
                </div>
                <div className="mt-2">
                  <div className="form-label">Redes</div>
                  {['linkedin','instagram','facebook','blog'].map((n) => (
                    <label key={n} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={generateForm.networks.includes(n)}
                        onChange={() => {
                          setGenerateForm((p) => {
                            const exists = p.networks.includes(n);
                            return { ...p, networks: exists ? p.networks.filter((x) => x !== n) : [...p.networks, n] };
                          });
                        }}
                      />
                      <span className="form-check-label text-capitalize ms-1">{n}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-2">
                  <label className="form-label">Objetivo</label>
                  <select className="form-select" value={generateForm.goal} onChange={(e) => setGenerateForm((p) => ({ ...p, goal: e.target.value }))}>
                    <option value="autoridad">Autoridad</option>
                    <option value="educacion">Educación</option>
                    <option value="visibilidad">Visibilidad</option>
                    <option value="leads">Leads</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setGenerateModal(false)}>Cancelar</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={generating}
                  onClick={async () => {
                    if (!selectedBrand) return;
                    if (!generateForm.fromDate || !generateForm.toDate || !generateForm.networks.length) {
                      alert('Rellena fechas y al menos una red');
                      return;
                    }
                    setGenerating(true);
                    try {
                      const res = await apiFetch(`/api/social/brands/${selectedBrand}/generate-plan`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify(generateForm)
                      });
                      const data = await parseJsonSafe(res);
                      if (res.status === 401) return handleUnauthorized();
                      if (!res.ok) alert((data && data.message) || 'Error generando plan');
                      else {
                        alert(`Generado: ${data.created} / Actualizado: ${data.updated}`);
                        fetchCalendar(selectedBrand);
                        setGenerateModal(false);
                      }
                    } catch (err) {
                      console.error('Error generando plan', err);
                      alert('Error de red generando plan');
                    } finally {
                      setGenerating(false);
                    }
                  }}
                >{generating ? 'Generando...' : 'Generar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {profileModal.open && (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Perfil {profileModal.network}</h5>
                <button type="button" className="btn-close" onClick={() => setProfileModal((p) => ({ ...p, open: false }))}></button>
              </div>
              <form onSubmit={saveProfile}>
                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Nombre de perfil</label>
                    <input className="form-control" value={profileModal.profileName} onChange={(e) => setProfileModal((p) => ({ ...p, profileName: e.target.value }))} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">URL</label>
                    <input className="form-control" value={profileModal.url} onChange={(e) => setProfileModal((p) => ({ ...p, url: e.target.value }))} required />
                  </div>
                  {profileModal.network === 'blog' && (
                    <div className="mb-2">
                      <label className="form-label">Tipo de blog</label>
                      <select className="form-select" value={profileModal.blogType} onChange={(e) => setProfileModal((p) => ({ ...p, blogType: e.target.value }))}>
                        <option value="wordpress">WordPress</option>
                        <option value="html">HTML</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  {profileModal.id && <button type="button" className="btn btn-danger me-auto" onClick={deleteProfile} disabled={saving.profile}>Eliminar</button>}
                  <button type="button" className="btn btn-secondary" onClick={() => setProfileModal((p) => ({ ...p, open: false }))} disabled={saving.profile}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={saving.profile}>{saving.profile ? 'Guardando...' : 'Guardar'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {eventModal.open && (
        <div className="modal d-block" tabIndex="-1">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{eventModal.mode === 'edit' ? 'Editar publicación' : 'Nueva publicación'}</h5>
                    <button type="button" className="btn-close" onClick={() => setEventModal({ open: false, mode: 'create', data: null })}></button>
              </div>
              <form onSubmit={saveEvent}>
                <div className="modal-body">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label">Fecha</label>
                      <input className="form-control" type="date" value={eventModal.data.date} onChange={(e) => setEventModal((p) => ({ ...p, data: { ...p.data, date: e.target.value } }))} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Hora</label>
                      <input className="form-control" type="time" value={eventModal.data.time || ''} onChange={(e) => setEventModal((p) => ({ ...p, data: { ...p.data, time: e.target.value } }))} />
                    </div>
                  </div>
                  <div className="row g-2 mt-2">
                    <div className="col-md-4">
                      <label className="form-label">Red</label>
                      <select className="form-select" value={eventModal.data.network} onChange={(e) => setEventModal((p) => ({ ...p, data: { ...p.data, network: e.target.value } }))}>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="blog">Blog</option>
                        <option value="multi">Multi</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Tipo</label>
                      <select className="form-select" value={eventModal.data.content_type || eventModal.data.contentType || 'post'} onChange={(e) => setEventModal((p) => ({ ...p, data: { ...p.data, content_type: e.target.value } }))}>
                        <option value="post">Post</option>
                        <option value="reel">Reel</option>
                        <option value="carrusel">Carrusel</option>
                        <option value="articulo">Artículo</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Estado</label>
                      <select className="form-select" value={eventModal.data.status || 'idea'} onChange={(e) => setEventModal((p) => ({ ...p, data: { ...p.data, status: e.target.value } }))}>
                        <option value="idea">Idea</option>
                        <option value="planificado">Planificado</option>
                        <option value="revisar">Revisar</option>
                        <option value="publicado">Publicado</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="form-label">Título</label>
                    <input className="form-control" value={eventModal.data.title} onChange={(e) => setEventModal((p) => ({ ...p, data: { ...p.data, title: e.target.value } }))} required />
                  </div>
                  <div className="mt-2">
                    <label className="form-label">Resumen</label>
                    <textarea className="form-control" value={eventModal.data.summary || ''} onChange={(e) => setEventModal((p) => ({ ...p, data: { ...p.data, summary: e.target.value } }))} />
                  </div>
                  <div className="mt-2">
                    <label className="form-label">Notas</label>
                    <textarea className="form-control" value={eventModal.data.notes || ''} onChange={(e) => setEventModal((p) => ({ ...p, data: { ...p.data, notes: e.target.value } }))} />
                  </div>
                  {eventModal.data.ai_generated && (
                    <div className="mt-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <h6 className="mb-0">Contenido IA</h6>
                        {eventModal.data.ai_risk ? <span className="badge bg-danger">Riesgo</span> : null}
                        {eventModal.data.qualityscore !== undefined && <span className="badge bg-info text-dark">QS: {eventModal.data.qualityscore}</span>}
                      </div>
                      {eventModal.data.ai_compliance_notes && (
                        <div className="alert alert-warning p-2">{eventModal.data.ai_compliance_notes}</div>
                      )}
                      <div className="mb-2">
                        <div className="fw-bold">LinkedIn</div>
                        <div className="border rounded p-2 bg-light mb-1" style={{ whiteSpace: 'pre-wrap' }}>{eventModal.data.ai_linkedin_body || '-'}</div>
                        <div className="text-muted small">Hashtags: {eventModal.data.ai_linkedin_hashtags || '-'}</div>
                      </div>
                      <div className="mb-2">
                        <div className="fw-bold">Instagram</div>
                        <div className="border rounded p-2 bg-light mb-1" style={{ whiteSpace: 'pre-wrap' }}>{eventModal.data.ai_instagram_caption || '-'}</div>
                        <div className="text-muted small">Hashtags: {eventModal.data.ai_instagram_hashtags || '-'}</div>
                        <div className="text-muted small">Formato: {eventModal.data.ai_instagram_format_hint || '-'}</div>
                      </div>
                      <div className="mb-2">
                        <div className="fw-bold">Facebook</div>
                        <div className="border rounded p-2 bg-light" style={{ whiteSpace: 'pre-wrap' }}>{eventModal.data.ai_facebook_copy || '-'}</div>
                      </div>
                      <div className="mb-2">
                        <div className="fw-bold">Blog</div>
                        <div className="border rounded p-2 bg-light mb-1" style={{ whiteSpace: 'pre-wrap' }}>{eventModal.data.ai_blog_outline || '-'}</div>
                        <div className="border rounded p-2 bg-light" style={{ whiteSpace: 'pre-wrap' }}>{eventModal.data.ai_blog_summary || '-'}</div>
                      </div>
                      {Array.isArray(eventModal.data.ai_variants) && eventModal.data.ai_variants.length > 0 && (
                        <div className="mt-3">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <h6 className="mb-0">Variantes</h6>
                            <span className="text-muted small">Regenerar aplica la variante elegida</span>
                          </div>
                          <div className="d-flex flex-column gap-2">
                            {eventModal.data.ai_variants.map((v, idx) => (
                              <div key={idx} className="border rounded p-2">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <span className="badge bg-secondary">Var {idx + 1}</span>
                                  {v.risk ? <span className="badge bg-danger">Riesgo</span> : null}
                                  {v.qualityScore !== undefined && <span className="badge bg-info text-dark">QS: {v.qualityScore}</span>}
                                </div>
                                {v.complianceNotes && <div className="text-muted small mb-1">{v.complianceNotes}</div>}
                                <div className="small fw-bold mb-1">LinkedIn</div>
                                <div className="small bg-light p-2 rounded mb-1" style={{ whiteSpace: 'pre-wrap' }}>{v.linkedin?.body || '-'}</div>
                                <div className="small fw-bold mb-1">Instagram</div>
                                <div className="small bg-light p-2 rounded mb-2" style={{ whiteSpace: 'pre-wrap' }}>{v.instagram?.caption || '-'}</div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => regenerateEvent(idx)}
                                  disabled={saving.event}
                                >
                                  Aplicar variante {idx + 1}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  {eventModal.mode === 'edit' && (
                    <>
                      <div className="me-auto d-flex gap-2">
                        <button type="button" className="btn btn-outline-secondary" onClick={duplicateEvent} disabled={saving.event}>Duplicar</button>
                        <button type="button" className="btn btn-danger" onClick={deleteEvent} disabled={saving.event}>Eliminar</button>
                        <button type="button" className="btn btn-outline-primary" onClick={() => regenerateEvent(0)} disabled={saving.event}>Regenerar IA</button>
                      </div>
                    </>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={() => setEventModal({ open: false, mode: 'create', data: null })}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={saving.event}>{saving.event ? 'Guardando...' : 'Guardar'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialPage;
